import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Handles drag-to-select over rows with autoscroll.
 * Exposes handlers you can wire directly to row container:
 *  - handleMouseDownOnRow(e, row)
 *  - handleMouseMoveOnRow(e, row)
 *  - handleMouseUpGlobal()
 *
 * Also exposes:
 *  - isDragging: boolean (true while mouse-drag selection is active)
 *  - justEndedDragRef: ref<boolean> (true for a short window after mouseup)
 */
export default function useDragSelectAutoscroll({
  wrapperRef,
  rows,
  charAdvancePx,
  onSelect, // fn(range or updater)
}) {
  //#region Refs for drag and autoscroll
  const draggingRef = useRef(false);
  const pointerXRef = useRef(null);
  const pointerYRef = useRef(null);

  const scrollParentRef = useRef(null);
  const autoScrollRAF = useRef(null);

  const [isDragging, setIsDragging] = useState(false);

  // NEW: suppression window right after drag ends
  const justEndedDragRef = useRef(false);
  const suppressTimerRef = useRef(0);
  //#endregion

  //#region Utils
  const getFirstScrollableAncestor = (node) => {
    let el = node;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === "auto" || overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight;
      if (isScrollable) return el;
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };

  const getAbsoluteIndexFromPointerCoordinates = useCallback(() => {
    const px = pointerXRef.current;
    const py = pointerYRef.current;
    if (px == null || py == null) return null;

    let target = document.elementFromPoint(px, py);
    if (!target) return null;

    let rowEl = target.closest?.(`[data-row-index]`);
    if (!rowEl) return null;

    const rowIndexAttr = rowEl.getAttribute("data-row-index");
    if (rowIndexAttr == null) return null;
    const rowIndex = Number(rowIndexAttr);
    const rowData = rows[rowIndex];
    if (!rowData) return null;

    const fwLine = rowEl.querySelector?.(`[class*="fwLine"]`);
    if (!fwLine) return null;

    const rect = fwLine.getBoundingClientRect();
    const adv = Math.max(1, charAdvancePx || 1);
    const idxInRow = Math.floor((px - rect.left) / adv);
    const clamped = Math.max(0, Math.min(rowData.textTop.length - 1, idxInRow));
    return rowData.start + clamped;
  }, [rows, charAdvancePx]);
  //#endregion

  //#region Autoscroll loop
  const startAutoScrollLoop = useCallback(() => {
    if (autoScrollRAF.current) return;
    const EDGE_PX = 48;
    const STEP_MAX = 28;

    const tick = () => {
      if (!draggingRef.current) {
        autoScrollRAF.current = null;
        return;
      }
      const sc = scrollParentRef.current;
      if (!sc) {
        autoScrollRAF.current = requestAnimationFrame(tick);
        return;
      }

      const rect = sc.getBoundingClientRect();
      const y = pointerYRef.current;
      const x = pointerXRef.current;

      const inside =
        typeof x === "number" &&
        typeof y === "number" &&
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom;

      let dy = 0;
      if (inside && typeof y === "number") {
        if (y < rect.top + EDGE_PX)
          dy = -Math.min(STEP_MAX, rect.top + EDGE_PX - y);
        else if (y > rect.bottom - EDGE_PX)
          dy = Math.min(STEP_MAX, y - (rect.bottom - EDGE_PX));
      }

      if (dy !== 0) {
        sc.scrollBy(0, dy);
        const absIdx = getAbsoluteIndexFromPointerCoordinates();
        if (absIdx != null) {
          onSelect?.((prev) =>
            prev
              ? { start: prev.start, end: absIdx }
              : { start: absIdx, end: absIdx }
          );
        }
      }

      autoScrollRAF.current = requestAnimationFrame(tick);
    };

    autoScrollRAF.current = requestAnimationFrame(tick);
  }, [getAbsoluteIndexFromPointerCoordinates, onSelect]);
  //#endregion

  //#region Public handlers
  const handleMouseDownOnRow = (e, row) => {
    if (!scrollParentRef.current) {
      scrollParentRef.current = getFirstScrollableAncestor(wrapperRef.current);
    }
    pointerXRef.current = e.clientX;
    pointerYRef.current = e.clientY;

    const lineEl = e.currentTarget.querySelector?.(`[class*="fwLine"]`);
    if (!lineEl) return;

    const rect = lineEl.getBoundingClientRect();
    const adv = Math.max(1, charAdvancePx || 1);
    const idxInRow = Math.floor((e.clientX - rect.left) / adv);
    const clamped = Math.max(0, Math.min(row.textTop.length - 1, idxInRow));
    const absoluteIdx = row.start + clamped;

    draggingRef.current = true;
    setIsDragging(true);
    justEndedDragRef.current = false; // reset any previous suppression
    if (suppressTimerRef.current) {
      clearTimeout(suppressTimerRef.current);
      suppressTimerRef.current = 0;
    }

    startAutoScrollLoop();

    onSelect && onSelect({ start: absoluteIdx, end: absoluteIdx });
    e.currentTarget.focus();
    e.preventDefault();

    // Safety: stop drag if mouseup happens outside
    const onWinUp = () => {
      handleMouseUpGlobal();
      window.removeEventListener("mouseup", onWinUp);
    };
    window.addEventListener("mouseup", onWinUp);
  };

  const handleMouseMoveOnRow = (e, row) => {
    if (!draggingRef.current) return;
    pointerXRef.current = e.clientX;
    pointerYRef.current = e.clientY;

    const lineEl = e.currentTarget.querySelector?.(`[class*="fwLine"]`);
    if (!lineEl) return;

    const rect = lineEl.getBoundingClientRect();
    const adv = Math.max(1, charAdvancePx || 1);
    const idxInRow = Math.floor((e.clientX - rect.left) / adv);
    const clamped = Math.max(0, Math.min(row.textTop.length - 1, idxInRow));
    const absoluteIdx = row.start + clamped;

    onSelect &&
      onSelect((prev) =>
        prev
          ? { start: prev.start, end: absoluteIdx }
          : { start: absoluteIdx, end: absoluteIdx }
      );
  };

  const handleMouseUpGlobal = () => {
    draggingRef.current = false;
    setIsDragging(false);

    // NEW: suppress auto-scroll for a short moment after drag ends
    justEndedDragRef.current = true;
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = setTimeout(() => {
      justEndedDragRef.current = false;
      suppressTimerRef.current = 0;
    }, 300);

    if (autoScrollRAF.current) {
      cancelAnimationFrame(autoScrollRAF.current);
      autoScrollRAF.current = null;
    }
  };
  //#endregion

  //#region Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoScrollRAF.current) cancelAnimationFrame(autoScrollRAF.current);
      if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
      draggingRef.current = false;
      setIsDragging(false);
      justEndedDragRef.current = false;
    };
  }, []);
  //#endregion

  return {
    handleMouseDownOnRow,
    handleMouseMoveOnRow,
    handleMouseUpGlobal,
    isDragging,
    justEndedDragRef, // expose for the viewer to consult
  };
}
